const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const safeDiv = (a, b) => (a !== null && b > 0 ? a / b : null);

const legFuel = (miles, mpg, price) => {
  if (miles === 0) return 0;
  if (mpg <= 0) return null;
  return (miles / mpg) * price;
};

export const OPTIONAL_KEYS = ["tolls", "permits", "hotel", "meals", "parking", "other"];

export function marginFraction(f) {
  const pct = f.margin_choice === "custom" ? parseFloat(f.custom_margin) : Number(f.margin_choice);
  if (!Number.isFinite(pct) || pct < 1 || pct > 99) return null;
  return pct / 100;
}

export const miles = (f) => {
  const L = num(f.loaded_miles);
  const D = num(f.deadhead_miles);
  const R = num(f.return_miles);
  return { L, D, R, total: L + D + R };
};

export const fuelCost = (f, m) => {
  const price = num(f.fuel_price);
  const emptyMpg = num(f.empty_mpg);
  const legs = [legFuel(m.L, num(f.loaded_mpg), price), legFuel(m.D, emptyMpg, price), legFuel(m.R, emptyMpg, price)];
  return legs.includes(null) ? null : legs.reduce((s, v) => s + v, 0);
};

export const maintenanceCost = (f, m) => m.total * num(f.maintenance_cpm);

export const driverPay = (f) => (f.driver_mode === "hourly" ? num(f.driver_rate) * num(f.driver_hours) : num(f.driver_flat));

export const extrasCost = (f) => OPTIONAL_KEYS.reduce((s, k) => s + num(f[k]), 0);

export const trueTripCost = (fuel, maintenance, driver, extras) => (fuel === null ? null : fuel + maintenance + driver + extras);

export const ratios = (offer, trueCost, m, margin) => {
  const profit = trueCost === null ? null : offer - trueCost;
  return {
    profit,
    beTotalCpm: safeDiv(trueCost, m.total),
    beLoadedCpm: safeDiv(trueCost, m.L),
    marginPct: safeDiv(profit, offer),
    revPerLoadedMile: safeDiv(offer, m.L),
    recommendedQuote: trueCost !== null && margin !== null ? trueCost / (1 - margin) : null,
  };
};

export const loadStatus = ({ offer, offerEntered, trueCost, recommendedQuote, loadedMiles }) => {
  if (trueCost === null || recommendedQuote === null || loadedMiles <= 0 || !offerEntered) return null;
  if (offer >= recommendedQuote) return "GOOD";
  if (offer > trueCost) return "MARGINAL";
  return "LOSING";
};

export function calculate(f) {
  const m = miles(f);
  const offer = num(f.offer);
  const fuel = fuelCost(f, m);
  const trueCost = trueTripCost(fuel, maintenanceCost(f, m), driverPay(f), extrasCost(f));
  const margin = marginFraction(f);
  const r = ratios(offer, trueCost, m, margin);
  const offerEntered = String(f.offer ?? "").trim() !== "";
  const status = loadStatus({ offer, offerEntered, trueCost, recommendedQuote: r.recommendedQuote, loadedMiles: m.L });

  return {
    offer, trueCost, totalMiles: m.total, loadedMiles: m.L, fuelCost: fuel, beTotalCpm: r.beTotalCpm,
    beLoadedCpm: r.beLoadedCpm, recommendedQuote: r.recommendedQuote, profit: r.profit, marginPct: r.marginPct,
    revPerLoadedMile: r.revPerLoadedMile, status, margin,
  };
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DASH = "—";

export const fmt = {
  money: (v) => (v === null || !Number.isFinite(v) ? DASH : usd.format(v)),
  cpm: (v) => (v === null || !Number.isFinite(v) ? DASH : `$${v.toFixed(3)}`),
  miles: (v) => (v === null || !Number.isFinite(v) ? DASH : `${v.toLocaleString("en-US", { maximumFractionDigits: 1 })} mi`),
  pct: (v) => (v === null || !Number.isFinite(v) ? DASH : `${(v * 100).toFixed(1)}%`),
};

export const round2 = (v) => Math.round(v * 100) / 100;
